import { useCallback } from 'react';

import type { ResponseError } from '@rtkwlf/asset-data-service';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { useOrganizationContext } from '@packages/legacy/organization-context';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';

import type { AssetsStatsExtended } from '../models';
import { useAssetDataServiceApi } from '../use-asset-data-service-api';

export const getAssetStatsKey = (organizationId: string) => [
  organizationId,
  'ads',
  'get-asset-stats',
];

const mockAssetStats = {
  data: 132,
  devices: 1604,
  network: 3476,
  users: 497,
  domains: 12,
  externalIps: 23,
};

export const useAssetStats = (
  options?: Omit<
    UseQueryOptions<AssetsStatsExtended, ResponseError>,
    'queryKey' | 'queryFn'
  >,
) => {
  const api = useAssetDataServiceApi();
  const { selectedOrganization } = useOrganizationContext();
  const organizationId = selectedOrganization.id;

  const [fullMockFF] = useFeatureFlag('ADS_FULL_MOCKED');

  const queryFn = useCallback(async () => {
    if (fullMockFF) {
      return mockAssetStats;
    }

    const { devices, domains, externalIps } = await api.getAssetStats({
      organizationId: selectedOrganization.id,
    });

    return {
      devices,
      domains,
      externalIps,
      // TODO: Use endpoint returned values for other asset types
      data: 0,
      network: 0,
      users: 0,
    };
  }, [api, fullMockFF, selectedOrganization.id]);

  return useQuery({
    queryFn,
    queryKey: getAssetStatsKey(organizationId),
    ...options,
  });
};
